import React, { useState, useEffect } from "react";
import { Flex, Text, Button, Separator, Progress } from "@radix-ui/themes";
import { ActiveUnifiIntegrations } from "./unifi/ActiveUnifiIntegrations";
import { ActiveBconnectIntegrations } from "./bconnect/ActiveBconnectIntegrations";
import { ActiveMailchimpIntegration } from "./mailchimp/ActiveMailchimpIntegration";
import { ActiveAirshipIntegration } from "./airship/ActiveAirshipIntegration";
import { ActiveMetaIntegration } from "./meta/ActiveMetaIntegration";
import { ActiveGoogleReviewsIntegration } from "./google-reviews/ActiveGoogleReviewsIntegration";
import { ActiveTripadvisorReviewsIntegration } from "./tripadvisor-reviews/ActiveTripadvisorReviewsIntegration";
import { useProfileContext } from "context";
import { useHubRiseConnections } from "./hubrise/useHubRiseConnections";
import { ActiveHubRiseIntegration } from "./hubrise/ActiveHubRiseIntegration";
import { ToastAlert } from "components";
import { useGetActiveIntegrations, useCheckCiscoIntegration, useGetAirshipStatus, useGetMetaStatus, useGetAtreemoStatus, 
        useGetKlaviyoStatus, useGetGoogleAdsStatus, useGetComoStatus, useGetBrevoStatus, useGetGoogleReviewsStatus, 
        useGetTripadvisorReviewsStatus, useGetDotdigitalStatus, useGetWhatsappCredentialsStatus, useGetLeatStatus,
        useGetSevenRoomsStatus, useGetBrazeStatus, useGetBloomreachStatus, useGetEatAppStatus, useGetMcpStatus } from "hooks";
import { useNavigate } from "react-router-dom";
import { ActiveCiscoMerakiIntegrations } from "./ciscomeraki/ActiveCiscoMerakiIntegrations";
import { ActiveTPLinkIntegrations } from "./tplink/ActiveTPLinkIntegration";
import { ActiveExtremeIntegrations } from "./extreme/ActiveExtremeIntegrations";
import { ActiveAtreemoIntegration } from "./atreemo/ActiveAtreemoIntegration";
import { ActiveKlaviyoIntegration } from "./klaviyo/ActiveKlaviyoIntegration";
import { ActiveComoIntegration } from "./como/ActiveComoIntegration";
import { ActiveBrevoIntegration } from "./brevo/ActiveBrevoIntegration";
import { ActiveDotdigitalIntegration } from "./dotdigital/ActiveDotdigitalIntegration";
import { ActiveLeatIntegration } from "./leat/ActiveLeatIntegration";
import { ActiveSevenRoomsIntegration } from "./sevenrooms/ActiveSevenRoomsIntegration";
import { ActiveGoogleAdsIntegration } from "./google_ads/ActiveGoogleAdsIntegration";
import { ActiveCambiumIntegrations } from "./cambium/ActiveCambiumIntegration";
import { ActiveWhatsappIntegration } from "./whatsapp/ActiveWhatsappIntegration";
import { ActiveBrazeIntegration } from "./braze/ActiveBrazeIntegration";
import { ActiveBloomreachIntegration } from "./bloomreach/ActiveBloomreachIntegration";
import { ActiveEatAppIntegration } from "./eat-app/ActiveEatAppIntegration";
import { ActiveClaudeIntegration } from "./claude/ActiveClaudeIntegration";

export default function ActiveIntegrations() {
  const { userProfile } = useProfileContext();
  const hubriseConnections = useHubRiseConnections(userProfile);
  const navigate = useNavigate();
  const { data: dotdigitalStatusData } = useGetDotdigitalStatus(userProfile.organisation_id);
  const { data: whatsappStatusData } = useGetWhatsappCredentialsStatus(userProfile.organisation_id);
  const { data: activeIntegrationsData, isLoading: isLoadingActiveIntegrations } = useGetActiveIntegrations(userProfile.organisation_id);
  const [activeIntegrations, setActiveIntegrations] = useState(false);
  const { data: ciscoIntegrationData, isLoading: isLoadingCiscoIntegration } = useCheckCiscoIntegration(userProfile.organisation_id);
  const { data: airshipStatusData, isLoading: isLoadingAirshipStatus } = useGetAirshipStatus(userProfile.organisation_id);
  const { data: metaStatusData, isLoading: isLoadingMetaStatus } = useGetMetaStatus(userProfile.organisation_id);
  const { data: atreemoStatusData, isLoading: isLoadingAtreemoStatus } = useGetAtreemoStatus(userProfile.organisation_id);
  const { data: klaviyoStatusData, isLoading: isLoadingKlaviyoStatus } = useGetKlaviyoStatus(userProfile.organisation_id);
  const { data: googleAdsStatusData, isLoading: isLoadingGoogleAdsStatus } = useGetGoogleAdsStatus(userProfile.organisation_id);
  const { data: comoStatusData, isLoading: isLoadingComoStatus } = useGetComoStatus(userProfile.organisation_id);
  const { data: brevoStatusData, isLoading: isLoadingBrevoStatus } = useGetBrevoStatus(userProfile.organisation_id);
  const { data: leatStatusData, isLoading: isLoadingLeatStatus } = useGetLeatStatus(userProfile.organisation_id);
  const { data: sevenroomsStatusData, isLoading: isLoadingSevenRoomsStatus } = useGetSevenRoomsStatus(userProfile.organisation_id);
  const { data: brazeStatusData, isLoading: isLoadingBrazeStatus } = useGetBrazeStatus(userProfile.organisation_id);
  const { data: bloomreachStatusData, isLoading: isLoadingBloomreachStatus } = useGetBloomreachStatus(userProfile.organisation_id);
  const { data: eatAppStatusData, isLoading: isLoadingEatAppStatus } = useGetEatAppStatus(userProfile.organisation_id);
  const { data: mcpStatusData, isLoading: isLoadingMcpStatus } = useGetMcpStatus(userProfile.organisation_id);
  const { data: googleReviewsStatusData, isLoading: isLoadingGoogleReviewsStatus } = useGetGoogleReviewsStatus(userProfile.organisation_id);
  const { data: tripadvisorReviewsStatusData, isLoading: isLoadingTripadvisorReviewsStatus } = useGetTripadvisorReviewsStatus(userProfile.organisation_id);

  const isIntegrationActive = (integrationType) => {
    if (integrationType === "hubrise") {
      return hubriseConnections.data?.some((connection) => connection.is_active) || false;
    }
    if (integrationType === "airship") {
      return airshipStatusData?.data?.is_active || false;
    }
    if (integrationType === "meta") {
      return metaStatusData?.data?.is_active || false;
    }
    if (integrationType === "atreemo") {
      return atreemoStatusData?.data?.is_active || false;
    }
    if (integrationType === "klaviyo") {
      return klaviyoStatusData?.data?.is_active || false;
    }
    if (integrationType === "como") {
      return comoStatusData?.data?.is_active || false;
    }
    if (integrationType === "google-ads") {
      return googleAdsStatusData?.data?.is_active || false;
    }
    if (integrationType === "brevo") {
      return brevoStatusData?.data?.is_active || false;
    }
    if (integrationType === "leat") {
      return leatStatusData?.data?.is_active || false;
    }
    if (integrationType === "sevenrooms") {
      return sevenroomsStatusData?.data?.is_active || false;
    }
    if (integrationType === "braze") {
      return brazeStatusData?.data?.is_active || false;
    }
    if (integrationType === "bloomreach") {
      return bloomreachStatusData?.data?.is_active || false;
    }
    if (integrationType === "eat-app") {
      return eatAppStatusData?.data?.is_active || false;
    }
    if (integrationType === "dotdigital") {
      return dotdigitalStatusData?.data?.is_active || false;
    }
    if (integrationType === "whatsapp") {
      return whatsappStatusData?.data?.is_active || false;
    }
    if (integrationType === "google-reviews") {
      return googleReviewsStatusData?.data?.status === "active";
    }
    if (integrationType === "tripadvisor-reviews") {
      return tripadvisorReviewsStatusData?.data?.status === "active";
    }
    if (integrationType === "claude" || integrationType === "mcp") {
      return mcpStatusData?.data?.is_active || false;
    }
    if (!activeIntegrationsData?.data?.integrations) return false;
    return activeIntegrationsData.data.integrations.some((integration) => integration.name === integrationType);
  };

  useEffect(() => {
    setActiveIntegrations(
      isIntegrationActive("mailchimp") || isIntegrationActive("unifi") || isIntegrationActive("bconnect") || 
      isIntegrationActive("cisco") || isIntegrationActive("airship") || isIntegrationActive("meta") || 
      isIntegrationActive("atreemo") || isIntegrationActive("extreme") || isIntegrationActive("klaviyo") ||
      isIntegrationActive("tplink") || isIntegrationActive("cambium") || isIntegrationActive("como") || 
      isIntegrationActive("google-ads") || isIntegrationActive("brevo") || isIntegrationActive("dotdigital") ||
      isIntegrationActive("leat") ||
      isIntegrationActive("sevenrooms") ||
      isIntegrationActive("braze") ||
      isIntegrationActive("bloomreach") ||
      isIntegrationActive("eat-app") ||
      isIntegrationActive("whatsapp") ||
      isIntegrationActive("google-reviews") || isIntegrationActive("tripadvisor-reviews") ||
      isIntegrationActive("claude") || isIntegrationActive("hubrise")
    );
  }, [activeIntegrationsData, ciscoIntegrationData, airshipStatusData, metaStatusData, atreemoStatusData, klaviyoStatusData, comoStatusData, googleAdsStatusData, googleReviewsStatusData, brevoStatusData, leatStatusData, sevenroomsStatusData, brazeStatusData, bloomreachStatusData, eatAppStatusData, dotdigitalStatusData, whatsappStatusData, tripadvisorReviewsStatusData, mcpStatusData, hubriseConnections.data]);

  if (!activeIntegrations) {
    if (hubriseConnections.isLoading) {
      return <Text size="2" color="gray">Loading connected integrations…</Text>;
    }
    return (
      <>
        <Flex style={{ alignSelf: "stretch", borderRadius: "var(--space-2)", border: "1px dashed var(--gray-a6)", flex: '1 0 0'}}
          height="100%" direction="column" justify="center" align="center" gap="5">
          <Flex direction="column" align="center" gap="2" maxWidth="400px">
            <Text size="4" weight="medium" align="center">No connected integrations</Text>
            <Text size="2" weight="regular" align="center" color="gray">
              After you connect and set up integrations, they'll show up here for you to manage.
            </Text>
            {hubriseConnections.isError && <Text size="2" color="red">Could not load HubRise connection status. Refresh to try again.</Text>}
          </Flex>
          <Button size="2" variant="soft" color="gray"
            onClick={() => navigate("/integrations/browse-all")}>
            Browse integrations
          </Button>
        </Flex>
        <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
          <ToastAlert />
        </div>
      </>
    );
  }

  // Simple loading state
  if (isLoadingActiveIntegrations || isLoadingCiscoIntegration || isLoadingAirshipStatus || isLoadingMetaStatus || isLoadingAtreemoStatus || isLoadingKlaviyoStatus || isLoadingGoogleAdsStatus || isLoadingGoogleReviewsStatus || isLoadingTripadvisorReviewsStatus || isLoadingComoStatus || isLoadingBrevoStatus || isLoadingLeatStatus || isLoadingSevenRoomsStatus || isLoadingBrazeStatus || isLoadingBloomreachStatus || isLoadingEatAppStatus || isLoadingMcpStatus) {
    return (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
    );
  }

  const hasWifiIntegrations = isIntegrationActive("unifi") || isIntegrationActive("bconnect") || 
    isIntegrationActive("cisco") || isIntegrationActive("tplink") || isIntegrationActive("extreme") || 
    isIntegrationActive("cambium");

  const hasCrmIntegrations = isIntegrationActive("mailchimp") || isIntegrationActive("airship") || 
    isIntegrationActive("meta") || isIntegrationActive("atreemo") || isIntegrationActive("klaviyo") || 
    isIntegrationActive("google-ads") || isIntegrationActive("como") || isIntegrationActive("brevo") ||
    isIntegrationActive("dotdigital") || isIntegrationActive("leat") || isIntegrationActive("sevenrooms") ||
    isIntegrationActive("braze") || isIntegrationActive("bloomreach") || isIntegrationActive("eat-app") || isIntegrationActive("whatsapp");

  const hasReviewsIntegrations = isIntegrationActive("google-reviews") || isIntegrationActive("tripadvisor-reviews");
  const hasAiIntegrations = isIntegrationActive("claude");

  return (
    <Flex direction="column" align="end" gap="5" style={{ alignSelf: "stretch", width: '100%', height: '100%', overflow: 'auto' }}>
      {isIntegrationActive("hubrise") && <>
        <Flex direction="column" gap="4" style={{ alignSelf: 'stretch' }}>
          <Text size="4" weight="medium">Restaurant ordering</Text>
          <ActiveHubRiseIntegration connections={hubriseConnections.data || []} />
        </Flex>
        <Separator size="4" />
      </>}
      {/* Wi-Fi & Presence Section */}
      <Flex direction="column" gap="4" style={{ alignSelf: 'stretch'}}>
        <Text size="4" weight="medium">Wi-Fi & Presence</Text>
        {hasWifiIntegrations ? (
          <>
            {isIntegrationActive("unifi") && <ActiveUnifiIntegrations />}
            {isIntegrationActive("bconnect") && <ActiveBconnectIntegrations />}
            {isIntegrationActive("cisco") && <ActiveCiscoMerakiIntegrations />}
            {isIntegrationActive("tplink") && <ActiveTPLinkIntegrations />}
            {isIntegrationActive("extreme") && <ActiveExtremeIntegrations />}
            {isIntegrationActive("cambium") && <ActiveCambiumIntegrations />}
          </>
        ) : (
          <Flex p="6" style={{ alignSelf: "stretch", borderRadius: "var(--radius-4)", border: "1px dashed var(--gray-a6)" }}
            direction="column" justify="center" align="center" gap="3">
            <Flex direction="column" align="center" gap="2" maxWidth="400px">
              <Text size="3" weight="medium" align="center">No active Wi-Fi integrations</Text>
              <Text size="2" align="center" color="gray">
                Connect a Wi-Fi integration for connection analytics or to track customers' presence and behavior.
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>
      
      {/* Separator */}
      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}} />

      {/* CRM & Marketing Section */}
      <Flex direction="column" gap="4" style={{ alignSelf: 'stretch'}}>
        <Text size="4" weight="medium">CRM & Marketing</Text>
        {hasCrmIntegrations ? (
          <>
            {isIntegrationActive("mailchimp") && <ActiveMailchimpIntegration />}
            {isIntegrationActive("airship") && <ActiveAirshipIntegration />}
            {isIntegrationActive("meta") && <ActiveMetaIntegration />}
            {isIntegrationActive("atreemo") && <ActiveAtreemoIntegration />}
            {isIntegrationActive("klaviyo") && <ActiveKlaviyoIntegration />}
            {isIntegrationActive("google-ads") && <ActiveGoogleAdsIntegration />}
            {isIntegrationActive("como") && <ActiveComoIntegration />}
            {isIntegrationActive("brevo") && <ActiveBrevoIntegration />}
            {isIntegrationActive("dotdigital") && <ActiveDotdigitalIntegration />}
            {isIntegrationActive("leat") && <ActiveLeatIntegration />}
            {isIntegrationActive("sevenrooms") && <ActiveSevenRoomsIntegration />}
            {isIntegrationActive("braze") && <ActiveBrazeIntegration />}
            {isIntegrationActive("bloomreach") && <ActiveBloomreachIntegration />}
            {isIntegrationActive("eat-app") && <ActiveEatAppIntegration />}
            {isIntegrationActive("whatsapp") && <ActiveWhatsappIntegration />}
          </>
        ) : (
          <Flex p="6" style={{ alignSelf: "stretch", borderRadius: "var(--space-2)", border: "1px dashed var(--gray-a6)" }}
            direction="column" justify="center" align="center" gap="3">
            <Flex direction="column" align="center" gap="2" maxWidth="400px">
              <Text size="3" weight="medium" align="center">No active CRM & Marketing integrations</Text>
              <Text size="2" align="center" color="gray">
                Connect a CRM or Marketing platform to manage customer relationships and ad-campaigns.
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>

      {/* Separator */}
      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}} />

      {/* Reviews Section */}
      <Flex direction="column" gap="4" style={{ alignSelf: 'stretch'}}>
        <Text size="4" weight="medium">Reviews</Text>
        {hasReviewsIntegrations ? (
          <>
            {isIntegrationActive("google-reviews") && <ActiveGoogleReviewsIntegration />}
            {isIntegrationActive("tripadvisor-reviews") && <ActiveTripadvisorReviewsIntegration />}
          </>
        ) : (
          <Flex p="6" style={{ alignSelf: "stretch", borderRadius: "var(--space-2)", border: "1px dashed var(--gray-a6)" }}
            direction="column" justify="center" align="center" gap="3">
            <Flex direction="column" align="center" gap="2" maxWidth="400px">
              <Text size="3" weight="medium" align="center">No active Reviews integrations</Text>
              <Text size="2" align="center" color="gray">
                Connect a reviews platform to manage and respond to customer reviews.
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>

      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}} />

      <Flex direction="column" gap="4" style={{ alignSelf: 'stretch'}}>
        <Text size="4" weight="medium">AI assistants</Text>
        {hasAiIntegrations ? (
          <>
            {isIntegrationActive("claude") && <ActiveClaudeIntegration />}
          </>
        ) : (
          <Flex p="6" style={{ alignSelf: "stretch", borderRadius: "var(--space-2)", border: "1px dashed var(--gray-a6)" }}
            direction="column" justify="center" align="center" gap="3">
            <Flex direction="column" align="center" gap="2" maxWidth="400px">
              <Text size="3" weight="medium" align="center">No active AI assistants</Text>
              <Text size="2" align="center" color="gray">
                Set up Claude from Browse all to ask read-only questions about your BConnect data.
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Flex>
  );
}
